import { getMobileOperatingSystem } from 'app/utils/utils';
import { ChakraStylesConfig, GroupBase } from 'chakra-react-select';
import { transparentize } from '@chakra-ui/theme-tools';
import { OPTION_HEIGHT_PIXELS } from './constants';
import { CustomComponentsProps } from './types';

interface GetStylesProps<Option> {
  customComponents?: CustomComponentsProps<Option>;
  customStyles?: ChakraStylesConfig<Option, false, GroupBase<Option>>;
}

export const getStyles = <Option extends {}>({
  customComponents,
  customStyles,
}: GetStylesProps<Option>): ChakraStylesConfig<
  Option,
  false,
  GroupBase<Option>
> => ({
  ...customStyles,
  valueContainer: (props, state) => ({
    ...props,
    px: 3,
    cursor: state.isDisabled ? 'default' : 'text',
    paddingLeft: customComponents?.inputLeftElement ? 0.5 : 3,
    ...customStyles?.valueContainer?.(props, state),
  }),
  dropdownIndicator: (props, state) => ({
    ...props,
    paddingRight: 0,
    paddingLeft: 2,
    fontSize: 'lg',
    cursor: state.isDisabled ? 'default' : 'pointer',
    bg: 'transparent',
    '> svg': {
      transform: `rotate(${state.selectProps.menuIsOpen ? -180 : 0}deg)`,
    },
    ...customStyles?.dropdownIndicator?.(props, state),
  }),
  menu: (props, state) => ({
    ...props,
    my: 2,
    shadow: 'lg',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 'dropdown',
    border: '1px solid',
    borderColor: 'borderBase',
    ...customStyles?.menu?.(props, state),
  }),
  menuList: (props, state) => ({
    ...props,
    padding: 0,
    overflowY: state.selectProps.disableVirtualization ? 'auto' : 'hidden',
    borderBottomRadius: 0,
    border: 'none',
    shadow: 'none',
    bg: 'surface1',
    ' div': {
      '::-webkit-scrollbar-track': {
        bg: 'surface2',
      },
      ':hover::-webkit-scrollbar-thumb': {
        bg: transparentize('M.60', 0.18),
        ':hover': {
          bg: transparentize('M.60', 0.54),
        },
        ':focus': {
          bg: transparentize('M.60', 0.72),
        },
      },
    },
    ...customStyles?.menuList?.(props, state),
  }),
  option: (props, state) => {
    const isMobileOS = Boolean(getMobileOperatingSystem());
    return {
      ...props,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: OPTION_HEIGHT_PIXELS,
      fontSize: 'sm',
      bg:
        state.isFocused && !isMobileOS
          ? transparentize('surface2', 0.72)
          : state.isSelected
          ? 'M.30'
          : 'surface2',
      maxWidth: '100%',
      ' div': {
        maxWidth: '100%',
      },
      _hover: {
        bg: transparentize('surface2', 0.72),
      },
      ...customStyles?.option?.(props, state),
    };
  },
  indicatorSeparator: (props, state) => ({
    display: 'none',
    ...customStyles?.indicatorSeparator?.(props, state),
  }),
  noOptionsMessage: (props, state) => ({
    ...props,
    display: 'flex',
    alignItems: 'center',
    fontSize: 'sm',
    textAlign: 'left',
    height: '49px',
    padding: '6px 16px',
    bg: 'M.30',
    color: 'textSubdued',
    ...customStyles?.noOptionsMessage?.(props, state),
  }),
  loadingIndicator: (props, state) => ({
    ...props,
    width: 4,
    height: 4,
    marginRight: 0.5,
    color: 'iconAccent',
    ...customStyles?.loadingIndicator?.(props, state),
  }),
  control: (props, state) => ({
    ...props,
    cursor: 'default',
    boxShadow: 'none',
    borderColor: 'M.30',
    bg: 'surface2',
    _hover: {
      borderColor: 'borderBase',
    },
    _focus: {
      borderColor: 'borderBase',
      boxShadow: 'none',
    },
    _invalid: {
      borderColor: 'R.80',
      boxShadow: 'none',
    },
    _disabled: {
      opacity: 1,
      color: 'M.40',
    },
    _focusWithin: {
      boxShadow: '0px 0px 8px 0px var(--chakra-colors-B-50)',
      ' input': {
        _focusVisible: {
          boxShadow: 'none',
        },
      },
    },
    ...customStyles?.control?.(props, state),
  }),
  input: (props, state) => ({
    ...props,
    width: '100%',
    textOverflow: 'ellipsis',
    ...customStyles?.input?.(props, state),
  }),
  inputContainer: (props, state) => ({
    ...props,
    display: 'flex',
    m: 0,
    '&::after': {
      display: 'none',
    },
    ...customStyles?.inputContainer?.(props, state),
  }),
  placeholder: (props, state) => ({
    ...props,
    color: state.isDisabled ? 'M.40' : 'textSubdued',
    ...customStyles?.placeholder?.(props, state),
  }),
  indicatorsContainer: (props, state) => ({
    ...props,
    paddingRight: 3,
    paddingLeft: 1,
    opacity: state.isDisabled ? 0.4 : 1,
    ...customStyles?.indicatorsContainer?.(props, state),
  }),
});
